import React, { FC, useCallback, useEffect, useState } from 'react';
import { MenuItem, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Alert, Cogs, Wrench } from 'mdi-material-ui';
import { SetDeviceResponses, SetTrayResponses } from 'api/Responses/SetResponses';
import { DirtInspectionIcon } from 'assets';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import DuplicateMergeModal from 'components/ConfirmModal/DuplicateMergeModal';
import DropDown from 'components/DropDown/DropDown';
import MemoizedPartRow from 'components/PartRow/PartRow';
import { useLanguage } from 'context/AppContext';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { SelectionToolbar } from './Table.styles';

interface PartTableProps {
  trayID: string;
  parts: SetDeviceResponses;
  // All trays in the set, used as move targets and to detect duplicate codes
  trays: SetTrayResponses;
  dispatchDraft: React.Dispatch<SetAction>;
  disabled: boolean;
}

/**
 *
 * @param parts the list of parts appearing in the part table
 * @returns a list of part IDs, sorted by part reference number
 */
const sortPartsByRef = (parts: SetDeviceResponses): string[] => {
  return Object.keys(parts).sort((partA, partB) => Number(parts[partA].ref) - Number(parts[partB].ref));
};

const PartTable: FC<PartTableProps> = ({ trayID, parts, trays, dispatchDraft, disabled }) => {
  const [language] = useLanguage();
  const sortedParts = sortPartsByRef(parts);
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // moveTarget is kept after closing so the dialog text stays stable while it fades out
  const [moveTarget, setMoveTarget] = useState<string>();
  const [moveOpen, setMoveOpen] = useState(false);

  // Ignore ids of parts that no longer exist in this tray
  const selectedInTray = selected.filter(id => parts[id]);
  const allSelected = sortedParts.length > 0 && selectedInTray.length === sortedParts.length;

  // Drop selected ids whose part is gone, so a re-added device does not come back ticked
  useEffect(() => setSelected(s => (s.some(id => !parts[id]) ? s.filter(id => parts[id]) : s)), [parts]);

  const toggleSelected = useCallback(
    (partID: string) =>
      setSelected(prev => (prev.includes(partID) ? prev.filter(id => id !== partID) : [...prev, partID])),
    [],
  );

  const moveSelected = (target: string): void => {
    dispatchDraft(draftActions.partMove(trayID, target, selectedInTray));
    setSelected([]);
  };

  const handleMoveTo = (target: string): void => {
    const hasDuplicates = selectedInTray.some(id => trays[target]?.parts[id]);
    if (hasDuplicates) {
      setMoveTarget(target);
      setMoveOpen(true);
    } else moveSelected(target);
  };

  const duplicateCodes = moveTarget ? selectedInTray.filter(id => trays[moveTarget]?.parts[id]) : [];

  return (
    <>
      {!disabled && selectedInTray.length > 0 && (
        <SelectionToolbar>
          <span>
            {selectedInTray.length} {language.DeviceTable.selected}
          </span>
          <Button onClick={() => setDeleteOpen(true)}>{language.DeviceTable.deleteSelected}</Button>
          {Object.keys(trays).length > 1 && (
            <DropDown
              displayEmpty
              inputProps={{ 'aria-label': language.DeviceTable.moveTo }}
              value=""
              onChange={event => handleMoveTo(event.target.value as string)}
              renderValue={() => language.DeviceTable.moveTo}
            >
              {Object.keys(trays)
                .filter(id => id !== trayID)
                .map(id => (
                  <MenuItem value={id} key={id}>
                    {id}
                  </MenuItem>
                ))}
            </DropDown>
          )}
        </SelectionToolbar>
      )}
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {!disabled && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedInTray.length > 0 && !allSelected}
                  onChange={() => setSelected(allSelected ? [] : sortedParts)}
                  inputProps={{ 'aria-label': language.DeviceTable.selectAll }}
                />
              </TableCell>
            )}
            <TableCell>{language.DeviceTable.reference}</TableCell>
            <TableCell align="center">{language.DeviceTable.image}</TableCell>
            <TableCell>{language.DeviceTable.code}</TableCell>
            <TableCell>{language.DeviceTable.description}</TableCell>
            <TableCell>{language.DeviceTable.quantity}</TableCell>

            <TableCell align="center">
              <DirtInspectionIcon />
            </TableCell>

            <TableCell align="center">
              <Cogs />
            </TableCell>

            <TableCell align="center">
              <Wrench />
            </TableCell>

            <TableCell align="center">
              <Alert />
            </TableCell>

            <TableCell>{language.DeviceTable.notes}</TableCell>

            {/* Empty header cell for the Actions column */}
            <TableCell className="actions" align="right" />
          </TableRow>
        </TableHead>
        <TableBody className="parts">
          {sortedParts.map(partID => (
            <MemoizedPartRow
              key={partID}
              trayID={trayID}
              partID={partID}
              part={parts[partID]}
              dispatchDraft={dispatchDraft}
              disabled={disabled}
              selected={selectedInTray.includes(partID)}
              onToggleSelected={toggleSelected}
            />
          ))}
        </TableBody>
      </Table>
      <ConfirmModal
        open={deleteOpen}
        close={() => setDeleteOpen(false)}
        confirm={() => {
          dispatchDraft(draftActions.partDeleteMany(trayID, selectedInTray));
          setSelected([]);
        }}
        confirmText={language.ConfirmModal.devices}
        buttonText={language.ConfirmModal.delete}
      />
      <DuplicateMergeModal
        open={moveOpen}
        close={() => setMoveOpen(false)}
        confirm={() => moveTarget && moveSelected(moveTarget)}
        tray={moveTarget ?? ''}
        codes={duplicateCodes}
      />
    </>
  );
};

const MemoizedPartTable = React.memo(PartTable);

export default MemoizedPartTable;
